'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        const isAuthPage = pathname.startsWith('/auth');
        const isLandingPage = pathname === '/';

        if (!session && !isAuthPage && !isLandingPage) {
          // Not logged in and trying to access app -> go to login
          router.push('/auth/login');
        } else if (session && isAuthPage) {
          // Already logged in and trying to access auth pages -> go to dashboard
          router.push('/overview');
        }
      } catch (error) {
        console.error('Auth check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        if (pathname.startsWith('/auth')) router.push('/overview');
      }
      if (event === 'SIGNED_OUT') {
        router.push('/');
      }
    });

    return () => subscription.unsubscribe();
  }, [pathname, router]);

  if (loading) {
    return (
      <div style={{ 
        height: '100vh', 
        width: '100vw', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#020617',
        color: 'white',
        gap: '20px'
      }}>
        <Loader2 className="animate-spin" size={40} color="#8b5cf6" />
        <p style={{ fontStyle: 'italic', opacity: 0.6 }}>Synchronizing with Tradearaidee Terminal...</p>
      </div>
    );
  }

  return <>{children}</>;
}
